// "Send to team" — the hold editor the agent fills in before a show request goes to the artist's team (ART-326,
// design 4d). Four tabs, pre-filled from the offer: Basics, Lineup, Assets (the contract), Terms — and an optional
// Note to the team at the bottom. NEVER the full show editor: the show does not exist yet, and only becomes one when
// Mgmt or the artist AV answers Approve or Hold & add crew. Everything here rides the OFFER (`…/offers/{id}/send`),
// which is why a request never touches the band's calendar while it waits.
//
// On a request already sent this is the same editor in "Save changes" mode — the team reads the updated request,
// and nobody is notified again.
//
// When the offer was one of several soft holds on the same night, sending it asks what to do with the others
// (design 4c): Keep them (in case the team passes) or Release them. Soft holds are only ever the agency's, so
// nobody outside it hears either way.
import { useState, type FormEvent } from "react";
import { X } from "lucide-react";
import { ActionModal, Button, DialogCancel, Dropzone, Field, IconButton, Modal, Picker, SegmentedControl, Textarea } from "@cntrl/ui";
import { TypeaheadInput } from "../booking/TypeaheadInput.js";
import { ActAvatar } from "../booking/actPhotos.js";
import { AcMark } from "../booking/AcMark.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import { useArtistSuggestions, useVenueSuggestions, venueMeta, type ActSuggestion, type VenueSuggestion } from "../booking/showSuggestions.js";
import { GoogleMapsAttribution, SpotifyAttribution } from "../shared/VendorAttribution.js";
import { mediaKindForFile, useUploadMedia } from "../../app/hooks/useMedia.js";
import {
  useBookingOffers,
  useReleaseOtherSoftHolds,
  useSendToTeam,
  type BookingOfferDto,
  type ShowRequestSlot,
} from "../../app/hooks/useBookingOffers.js";
import { pushToast } from "../../app/toasts.js";
import { useBandContext } from "../../app/bandContext.js";
import { useResolvedOrg, useRosterArtists } from "../../app/hooks/useRoster.js";
import { formatDay } from "./rosterFormat.js";
import { SLOT_ROLES } from "../booking/sharedShowFormat.js";
import styles from "./SendToTeamModal.module.scss";

const TABS = [
  { value: "basics", label: "Basics" },
  { value: "lineup", label: "Lineup" },
  { value: "assets", label: "Assets" },
  { value: "terms", label: "Terms" },
];

const HOLD_LEVELS = [
  { value: "1", label: "1st hold" },
  { value: "2", label: "2nd hold" },
  { value: "3", label: "3rd hold" },
];

function centsToInput(c: number | null | undefined) {
  return c == null ? "" : (c / 100).toFixed(2);
}
function inputToCents(v: string): number | null {
  const t = v.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
function inputToInt(v: string): number | null {
  const t = v.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n) : null;
}

interface SlotDraft {
  slotRole: string;
  artistName: string;
  /** What the act search picked (ART-330) — the same ids the show editor's lineup stores. */
  spotifyArtistId: string | null;
  friendBandId: string | null;
  spotifyImageUrl: string | null;
  /** The picked band's photo id — display only, never sent (the hold stores the band, not its picture). */
  avatarMediaId?: string | null;
  /** Set lengths are not asked for yet (product owner) — a length already on the request is kept. */
  minutes: number | null;
}

/** The picked venue — what the show editor's venue search carries onto the hold. */
interface VenuePick {
  placeId: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  lat: number | null;
  lng: number | null;
}

export interface SendToTeamModalProps {
  offer: BookingOfferDto;
  onClose: () => void;
  /** After a successful send / save. */
  onSent?: (offer: BookingOfferDto) => void;
}

export function SendToTeamModal({ offer, onClose, onSent }: SendToTeamModalProps) {
  const send = useSendToTeam();
  const release = useReleaseOtherSoftHolds();
  const board = useBookingOffers();
  const { uploadMedia } = useUploadMedia();
  const existing = offer.request;
  const alreadySent = offer.status === "requested";

  const [tab, setTab] = useState("basics");
  // ART-330 — the offer names the BUYER; the venue is picked here. A request sent before that carried its venue in
  // the offer's own field, so it opens with that.
  const [venue, setVenue] = useState(existing?.venue?.name ?? (alreadySent ? offer.buyerVenue ?? "" : ""));
  const [venuePick, setVenuePick] = useState<VenuePick | null>(existing?.venue
    ? {
        placeId: existing.venue.placeId ?? null, city: existing.venue.city ?? null, state: existing.venue.state ?? null,
        country: existing.venue.country ?? null, lat: existing.venue.lat ?? null, lng: existing.venue.lng ?? null,
      }
    : null);
  const [date, setDate] = useState(offer.date ?? "");
  const [holdTier, setHoldTier] = useState(String(existing?.holdTier ?? 1));
  const [capacity, setCapacity] = useState(existing?.capacity != null ? String(existing.capacity) : "");
  const [promoter, setPromoter] = useState(existing?.promoter ?? (alreadySent ? "" : offer.buyerVenue ?? ""));
  // The artist the show is for leads the lineup, as the headliner by default (ART-330) — its slot role is editable,
  // the act is not. It is marked by its own band id, which is how the hold matches it to the band's own slot.
  const { bandId } = useBandContext();
  const { orgId } = useResolvedOrg();
  const self = useRosterArtists(orgId).data?.find((a) => a.bandId === bandId);
  const bandName = self?.name ?? "";
  const selfAvatar = self?.avatarMediaId ?? null;
  const [slots, setSlots] = useState<SlotDraft[]>(() => {
    const saved: SlotDraft[] = (existing?.lineup ?? []).map((s) => ({
      slotRole: s.slotRole ?? "support",
      artistName: s.artistName ?? "",
      spotifyArtistId: s.spotifyArtistId ?? null,
      friendBandId: s.friendBandId ?? null,
      spotifyImageUrl: s.spotifyImageUrl ?? null,
      minutes: s.minutes ?? null,
    }));
    if (saved.some((s) => s.friendBandId === bandId)) return saved;
    return [
      { slotRole: "headliner", artistName: "", spotifyArtistId: null, friendBandId: bandId, spotifyImageUrl: null, minutes: null },
      ...saved,
    ];
  });
  const isSelf = (s: SlotDraft) => s.friendBandId === bandId;
  const [contractMediaId, setContractMediaId] = useState<string | null>(existing?.contractMediaId ?? null);
  const [uploading, setUploading] = useState(false);
  const [amount, setAmount] = useState(centsToInput(offer.amountCents));
  const [deposit, setDeposit] = useState(centsToInput(existing?.depositCents));
  const [depositDue, setDepositDue] = useState(existing?.depositDueOn ?? "");
  const [doorPct, setDoorPct] = useState(offer.doorSplitPct != null ? String(offer.doorSplitPct) : "");
  const [doorAfter, setDoorAfter] = useState(centsToInput(existing?.doorSplitAfterCents));
  const [ticket, setTicket] = useState(centsToInput(existing?.ticketPriceCents));
  const [radius, setRadius] = useState(existing?.contractTerms ?? "");
  const [note, setNote] = useState(existing?.noteToTeam ?? "");
  const [askOthers, setAskOthers] = useState<{ sent: BookingOfferDto; others: BookingOfferDto[] } | null>(null);

  const terms = offer.dealType === "terms";

  async function onContract(file: File) {
    setUploading(true);
    try {
      const asset = await uploadMedia(file, { kind: mediaKindForFile(file), purpose: "show-contract" });
      setContractMediaId(asset.mediaId ?? null);
    } catch (e) {
      pushToast(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!offer.offerId || !date) return;
    const lineup: ShowRequestSlot[] = slots
      .filter((s) => isSelf(s) || s.artistName.trim() !== "")
      .map((s) => ({
        slotRole: s.slotRole,
        artistName: isSelf(s) ? bandName || s.artistName.trim() : s.artistName.trim(),
        spotifyArtistId: s.spotifyArtistId,
        friendBandId: s.friendBandId,
        spotifyImageUrl: s.spotifyImageUrl,
        minutes: s.minutes,
      }));
    send.mutate(
      {
        offerId: offer.offerId,
        body: {
          offer: {
            date,
            // The offer keeps naming its BUYER (ART-330); the venue rides the request.
            buyerVenue: offer.buyerVenue ?? null,
            amountCents: inputToCents(amount),
            dealType: offer.dealType,
            doorSplitPct: terms ? (doorPct.trim() === "" ? null : Number(doorPct)) : null,
            kind: offer.kind,
            goodThrough: offer.goodThrough ?? null,
            details: offer.details ?? null,
          },
          details: {
            holdTier: Number(holdTier),
            capacity: inputToInt(capacity),
            promoter: promoter.trim() || null,
            ticketPriceCents: inputToCents(ticket),
            depositCents: inputToCents(deposit),
            depositDueOn: depositDue || null,
            doorSplitAfterCents: terms ? inputToCents(doorAfter) : null,
            contractTerms: radius.trim() || null,
            contractMediaId,
            lineup,
            noteToTeam: note.trim() || null,
            venue: venuePick || venue.trim() ? { ...(venuePick ?? {}), name: venue.trim() || null } : undefined,
          },
        },
      },
      {
        onSuccess: (sent) => {
          onSent?.(sent);
          // Other soft holds on the same night → ask Keep or Release (design 4c). Only on the FIRST send.
          const others = alreadySent
            ? []
            : (board.data?.offers ?? []).filter(
                (o) => o.status === "soft_hold" && o.date === date && o.offerId !== offer.offerId,
              );
          if (others.length > 0) setAskOthers({ sent, others });
          else onClose();
        },
      },
    );
  }

  if (askOthers) {
    const venueName = askOthers.sent.buyerVenue ?? "This offer";
    return (
      <ActionModal
        title="Other soft holds that night"
        onCancel={onClose}
        hideCancel
        actions={[
          { label: "Keep", onClick: onClose },
          {
            label: "Release",
            primary: true,
            loading: release.isPending,
            onClick: () => release.mutate(askOthers.sent.offerId!, { onSuccess: onClose }),
          },
        ]}
      >
        <p className={styles.askLead}>
          {venueName} is going to the team. What about the other soft holds on {formatDay(date)}?
        </p>
        <ul className={styles.askList}>
          {askOthers.others.map((o) => (
            <li key={o.offerId}>{o.buyerVenue ?? "Untitled"}</li>
          ))}
        </ul>
        <p className={styles.askNote}>Soft holds are only visible to your agency. Nobody else is told.</p>
      </ActionModal>
    );
  }

  return (
    <Modal
      className={styles.modal}
      title={alreadySent ? "Edit request" : "Send to team"}
      size="md"
      onClose={onClose}
      bodyAs="form"
      onSubmit={submit}
      footer={
        <>
          <span className={styles.notifies}>
            {alreadySent ? "The team sees the updated request." : "Notifies the artist's management and Artist AV."}
          </span>
          <DialogCancel />
          <Button type="submit" loading={send.isPending} disabled={!date || uploading}>
            {alreadySent ? "Save changes" : "Send to team"}
          </Button>
        </>
      }
    >
      <SegmentedControl label="Section" value={tab} onChange={setTab} options={TABS} />

      {tab === "basics" && (
        <div className={styles.grid}>
          {/* The show editor's own venue search (ART-330): a pick carries the place, its town and its coordinates
              onto the hold, so the request links to the venue exactly as the editor would. */}
          <div className={styles.venue}>
            <TypeaheadInput<VenueSuggestion>
              label="Venue"
              placeholder="Venue name"
              value={venue}
              onChange={(t) => {
                setVenue(t);
                setVenuePick(null);
              }}
              onPick={(v) => {
                setVenue(v.name ?? "");
                setVenuePick({
                  placeId: v.placeId ?? null, city: v.city ?? null, state: v.state ?? null,
                  country: v.country ?? null, lat: v.lat ?? null, lng: v.lng ?? null,
                });
                if (v.capacity != null && capacity.trim() === "") setCapacity(String(v.capacity));
              }}
              useSuggestions={useVenueSuggestions}
              itemText={(v) => v.name ?? ""}
              itemKey={(v, i) => v.placeId ?? `${v.name}-${i}`}
              itemPrimary={(v) => v.name}
              itemSecondary={(v) => venueMeta(v) || null}
              footer={<GoogleMapsAttribution />}
            />
            {venuePick && (venuePick.city || venuePick.state) && (
              <p className={styles.venueWhere}>{[venuePick.city, venuePick.state].filter(Boolean).join(", ")}</p>
            )}
          </div>
          <Field label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          <Picker label="Hold" value={holdTier} onChange={setHoldTier} options={HOLD_LEVELS} />
          {/* Good through is the deal's ONLY expiry (product owner). Read-only here — to extend it, edit the offer. */}
          <Field
            label="Good through"
            value={offer.goodThrough ? `${formatDay(offer.goodThrough)} · from offer` : "Not set on the offer"}
            readOnly
          />
          <Field label="Capacity" type="number" min={0} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          <Field label="Promoter / buyer" value={promoter} onChange={(e) => setPromoter(e.target.value)} maxLength={200} />
        </div>
      )}

      {tab === "lineup" && (
        <div className={styles.lineup}>
          {slots.map((s, i) => (
            <div key={i} className={styles.slot} data-self={isSelf(s) || undefined}>
              <Picker
                label="Slot"
                hideLabel
                value={s.slotRole}
                onChange={(v) => setSlots((all) => all.map((x, j) => (j === i ? { ...x, slotRole: v } : x)))}
                options={SLOT_ROLES}
              />
              {/* A settled act is a pill — its photo, its name and the AC mark when it is on Artist CNTRL — the way
                  the show editor draws the bill (ART-330). The artist's own act is always settled; a searched act
                  settles when it is picked, and its ✕ takes it back off. */}
              {isSelf(s) || s.spotifyArtistId || s.friendBandId ? (
                <ActPill
                  name={isSelf(s) ? bandName || "This artist" : s.artistName}
                  source={isSelf(s)
                    ? { kind: "band", bandId, mediaId: selfAvatar }
                    : s.friendBandId
                      ? { kind: "band", bandId: s.friendBandId, mediaId: s.avatarMediaId ?? null }
                      : { kind: "vendor", url: s.spotifyImageUrl }}
                  onCntrl={isSelf(s) || !!s.friendBandId}
                />
              ) : (
              /* The show editor's act search (ART-330): friends, then any band on Artist CNTRL, then Spotify. */
              <TypeaheadInput<ActSuggestion>
                ariaLabel="Act"
                placeholder="Search acts…"
                value={s.artistName}
                onChange={(t) => setSlots((all) => all.map((x, j) => (j === i
                  ? { ...x, artistName: t, spotifyArtistId: null, friendBandId: null, spotifyImageUrl: null }
                  : x)))}
                onPick={(a) => setSlots((all) => all.map((x, j) => (j === i
                  ? {
                      ...x, artistName: a.name, spotifyArtistId: a.spotifyArtistId, friendBandId: a.friendBandId,
                      spotifyImageUrl: a.imageUrl, avatarMediaId: a.avatarMediaId,
                    }
                  : x)))}
                useSuggestions={useArtistSuggestions}
                itemText={(a) => a.name}
                itemKey={(a, k) => a.friendBandId ?? a.spotifyArtistId ?? `${a.name}-${k}`}
                itemPrimary={(a) => (
                  <span className={styles.actOption}>
                    <ActAvatar
                      source={a.friendBandId
                        ? { kind: "band", bandId: a.friendBandId, mediaId: a.avatarMediaId }
                        : { kind: "vendor", url: a.imageUrl }}
                      initials={orgInitials(a.name)}
                      size="sm"
                      shape="circle"
                    />
                    {a.name}
                  </span>
                )}
                itemSecondary={(a) => (a.isFriend ? "Friend" : a.friendBandId ? "On Artist CNTRL" : null)}
                footer={<SpotifyAttribution />}
              />
              )}
              {isSelf(s) ? (
                <span aria-hidden />
              ) : (
                <IconButton
                  icon={X}
                  label={`Remove ${s.artistName || "act"}`}
                  size="sm"
                  onClick={() => setSlots((all) => all.filter((_, j) => j !== i))}
                />
              )}
            </div>
          ))}
          <button
            type="button"
            className={styles.addAct}
            onClick={() => setSlots((all) => [
              ...all,
              { slotRole: "support", artistName: "", spotifyArtistId: null, friendBandId: null, spotifyImageUrl: null, minutes: null },
            ])}
          >
            + Add act
          </button>
        </div>
      )}

      {tab === "assets" && (
        <div>
          {contractMediaId ? (
            <div className={styles.attached}>
              <span>Contract attached</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setContractMediaId(null)}>Remove</Button>
            </div>
          ) : (
            <Dropzone
              density="compact"
              accept=".pdf,application/pdf,.doc,.docx,image/*"
              disabled={uploading}
              label="Contract"
              prompt={uploading ? "Uploading…" : "Drop the contract here, or click to browse"}
              hint="PDF, DOC or a photo of the signed page"
              rejectMessage="That file type is not accepted here."
              onFile={(f) => void onContract(f)}
            />
          )}
        </div>
      )}

      {tab === "terms" && (
        <div className={styles.grid}>
          <Field label={terms ? "Guarantee" : "Guarantee"} type="number" inputMode="decimal" min={0} step="0.01"
            affixStart="$" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Field label="Deposit" type="number" inputMode="decimal" min={0} step="0.01" affixStart="$"
            value={deposit} onChange={(e) => setDeposit(e.target.value)} />
          <Field label="Deposit due" type="date" value={depositDue} onChange={(e) => setDepositDue(e.target.value)} />
          {terms && (
            <>
              <Field label="Door split" type="number" min={0} max={100} affixEnd="%" value={doorPct}
                onChange={(e) => setDoorPct(e.target.value)} />
              <Field label="Door split after" type="number" min={0} step="0.01" affixStart="$" value={doorAfter}
                onChange={(e) => setDoorAfter(e.target.value)} />
            </>
          )}
          <Field label="Ticket price" type="number" inputMode="decimal" min={0} step="0.01" affixStart="$"
            value={ticket} onChange={(e) => setTicket(e.target.value)} />
          <Field label="Radius / notes" value={radius} onChange={(e) => setRadius(e.target.value)} maxLength={4000}
            placeholder="90 mi · 60 days" />
        </div>
      )}

      <Textarea
        label="Note to the team (optional)"
        rows={3}
        value={note}
        maxLength={1000}
        onChange={(e) => setNote(e.target.value)}
        hint="Shows on the request."
      />
    </Modal>
  );
}

/** A settled act on the bill — photo, name, and the AC mark when the act is a band on Artist CNTRL. */
function ActPill({ name, source, onCntrl }: {
  name: string;
  source: Parameters<typeof ActAvatar>[0]["source"];
  onCntrl: boolean;
}) {
  return (
    <span className={styles.actPill}>
      <ActAvatar source={source} initials={orgInitials(name || "?")} size="sm" shape="circle" />
      <span className={styles.actPillName}>{name}</span>
      {onCntrl && <AcMark title={`${name} is on Artist CNTRL`} />}
    </span>
  );
}
